import { Stack } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";

interface TimesheetRowProps {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
  hasError?: boolean;
  highlight?: boolean;
}

function getLabelColor(theme: Theme, props: { muted: boolean; hasError: boolean; bold: boolean }) {
  if (props.muted) {
    return theme.palette.text.tertiary;
  }

  if (props.hasError) {
    return theme.palette.intent?.error.text;
  }

  if (props.bold) {
    return theme.palette.text.primary;
  }

  return theme.palette.text.secondary;
}

function getValueColor(theme: Theme, props: { muted: boolean; highlight: boolean; bold: boolean }) {
  if (props.muted) {
    return theme.palette.text.tertiary;
  }

  if (props.highlight) {
    return theme.palette.shift?.block.border;
  }

  if (props.bold) {
    return theme.palette.text.primary;
  }

  return theme.palette.text.secondary;
}

export function TimesheetRow(props: TimesheetRowProps) {
  const { label, value, bold = false, muted = false, hasError = false, highlight = false } = props;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={1} alignItems="center">
        <Text
          semibold={bold}
          variant="body2"
          sx={(theme) => ({
            color: getLabelColor(theme, { muted, hasError, bold }),
          })}
        >
          {label}
        </Text>
        {hasError && (
          <CbhIcon
            type="triangle-exclamation"
            size="xSmall"
            color={(theme) => theme.palette.intent?.error.text}
          />
        )}
      </Stack>
      <Text
        semibold={bold}
        variant="body2"
        sx={(theme) => ({
          color: getValueColor(theme, { muted, highlight, bold }),
        })}
      >
        {value}
      </Text>
    </Stack>
  );
}
